import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Brain, Ear, Zap, Check, Target, Clock, Users, Volume2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../i18n/translations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stats data
const statsData = [
  { icon: Volume2, value: '0h', label: "d'entraînement audio", desc: "Contrairement à la vidéo, omniprésente" },
  { icon: Users, value: '73%', label: 'des coachs', desc: "Constatent le problème de communication" },
  { icon: Target, value: '8-12', label: 'opportunités', desc: "Perdues par match à cause du bruit" },
  { icon: Clock, value: '0.01s', label: 'de différence', desc: "Entre gagner et perdre" }
];

// Challenges data
const challengesData = [
  { title: 'Environnement hostile', desc: '80 000 spectateurs, chants, insultes... Comment isoler les instructions tactiques dans le chaos sonore d\'un stade ?', example: 'Ex: Stade de Bayonne' },
  { title: 'Nouveaux coéquipiers', desc: 'Un joueur international change de club tous les 2-3 ans. Comment recréer les automatismes avec de nouvelles voix ?', example: 'Ex: Reconnaître la voix du nouveau n°10' },
  { title: 'Phases chaotiques', desc: 'Après un ruck gratté, plus personne n\'est en position standard. Comment repositionner l\'équipe instantanément ?', example: 'Ex: Le n°9 devient n°12' }
];

// Briques data
const briquesData = [
  { icon: Ear, title: 'Éducation Auditive', subtitle: 'Fondamentaux', desc: 'Développez les réflexes auditifs de vos joueurs : spatialisation sonore, reconnaissance instantanée, automatismes décisionnels.', features: ['Audio 3D immersif', 'Exercices progressifs', 'Mesure de performance'], color: 'primary' },
  { icon: Brain, title: 'Programme Club', subtitle: 'Sur mesure', desc: 'Intégration complète de votre playbook, scénarios spécifiques à votre équipe, préparation personnalisée.', features: ['Votre playbook', 'Vos phases de jeu', 'Votre stratégie'], color: 'secondary' },
  { icon: Zap, title: 'Flexibilité Tactique', subtitle: 'Avantage compétitif', desc: 'Permutation dynamique des postes, préparation aux situations chaotiques, surprise tactique.', features: ['Scénarios atypiques', 'Adaptabilité totale', 'Longueur d\'avance'], color: 'accent' }
];

// Use cases data
const useCasesData = [
  { title: 'Intégration nouveau joueur', problem: 'Joueur d\'un autre pays ne connaît pas les coéquipiers', solution: 'Skills audio avant l\'entraînement physique', result: 'Intégration tactique accélérée' },
  { title: 'Match haute pression', problem: 'Stade hostile, 80 000 spectateurs', solution: 'Entraînement avec environnement sonore réaliste', result: 'Isoler les instructions malgré le bruit' },
  { title: 'Phase chaotique', problem: 'Après un ruck gratté, joueurs mal positionnés', solution: 'Scénarios pré-entraînés avec permutation', result: 'Réaction automatique' },
  { title: 'Surprise tactique', problem: 'L\'adversaire a analysé vos phases standards', solution: 'Scénario inversé (ex: n°10↔n°12)', result: 'Adversaire désorganisé' }
];

// Hero Background Animation - Style Coming Soon
const HeroBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base background */}
      <div className="absolute inset-0 bg-[#0a0c10]" />
      
      {/* Gradient orbs */}
      <motion.div 
        className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-primary/20 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.3, 0.2]
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div 
        className="absolute bottom-1/4 right-1/4 w-[600px] h-[600px] bg-secondary/20 rounded-full blur-3xl"
        animate={{ 
          scale: [1.2, 1, 1.2],
          opacity: [0.15, 0.25, 0.15]
        }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />
      <motion.div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-teal-500/10 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.3, 1],
          opacity: [0.1, 0.2, 0.1]
        }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />
      
      {/* Animated sound wave lines */}
      <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
        {[...Array(25)].map((_, i) => (
          <motion.path
            key={i}
            d={`M0 ${540 + (i - 12) * 25} Q480 ${540 + Math.sin(i * 0.5) * 120} 960 ${540 + (i - 12) * 25} T1920 ${540 + (i - 12) * 25}`}
            stroke={i % 2 === 0 ? "#14b8a6" : "#f97316"}
            strokeWidth="1.5"
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: [0.3, 0.6, 0.3] }}
            transition={{ 
              duration: 3 + i * 0.2, 
              delay: i * 0.1, 
              repeat: Infinity, 
              repeatType: "reverse",
              ease: "easeInOut"
            }}
          />
        ))}
      </svg>
      
      {/* Floating particles */}
      <div className="absolute inset-0">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-teal-400 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.5, 1]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0c10]/50 to-[#0a0c10]" />
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, value, label, desc, index }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
    <Card className="card-glass p-6 h-full text-center">
      <Icon className="w-8 h-8 text-primary mx-auto mb-4" />
      <div className="text-4xl font-bold text-gradient mb-2">{value}</div>
      <div className="text-white font-medium mb-1">{label}</div>
      <p className="text-white/40 text-sm">{desc}</p>
    </Card>
  </motion.div>
);

// Challenge Card Component
const ChallengeCard = ({ title, desc, example, index }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
    <Card className="card-glass h-full p-8">
      <h3 className="text-xl font-semibold text-white mb-4">{title}</h3>
      <p className="text-white/50 leading-relaxed mb-4">{desc}</p>
      <p className="text-primary/70 text-sm italic">{example}</p>
    </Card>
  </motion.div>
);

// Brique Card Component
const BriqueCard = ({ icon: Icon, title, subtitle, desc, features, color, index }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.15 }}>
    <Card className="card-glass h-full p-8 hover:border-white/20 transition-colors">
      <div className={`w-14 h-14 rounded-2xl bg-${color}/10 flex items-center justify-center mb-6`}>
        <Icon className={`w-7 h-7 text-${color}`} />
      </div>
      <div className={`text-${color} text-sm font-medium mb-2`}>{subtitle}</div>
      <h3 className="text-2xl font-semibold text-white mb-4">{title}</h3>
      <p className="text-white/50 leading-relaxed mb-6">{desc}</p>
      <ul className="space-y-2">
        {features.map((f, j) => (
          <li key={j} className="flex items-center gap-2 text-white/40 text-sm">
            <Check className={`w-4 h-4 text-${color}`} />{f}
          </li>
        ))}
      </ul>
    </Card>
  </motion.div>
);

// Use Case Card Component
const UseCaseCard = ({ title, problem, solution, result, index, lang }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
    <Card className="card-glass p-6 h-full">
      <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
      <div className="space-y-3 text-sm">
        <div className="flex gap-3"><span className="text-red-400 font-medium shrink-0">{t('home.problem', lang)}</span><span className="text-white/50">{problem}</span></div>
        <div className="flex gap-3"><span className="text-primary font-medium shrink-0">{t('home.solution', lang)}</span><span className="text-white/50">{solution}</span></div>
        <div className="flex gap-3"><span className="text-secondary font-medium shrink-0">{t('home.result', lang)}</span><span className="text-white/60">{result}</span></div>
      </div>
    </Card>
  </motion.div>
);

const Home = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({ club_name: '', contact_name: '', email: '', phone: '', level: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Marketing baseline - next to logo
  const marketingBaseline = {
    fr: 'L\'oreille qui gagne des matchs',
    eu: 'Partidak irabazten dituen belarria',
    en: 'The ear that wins games',
    es: 'El oído que gana partidos',
    it: 'L\'orecchio che vince le partite',
    ja: '勝利をもたらす耳',
    de: 'Das Ohr, das Spiele gewinnt',
    ru: 'Ухо, которое выигрывает матчи',
    ka: 'ყური, რომელიც მატჩებს იგებს'
  };

  const marketingSubline = {
    fr: 'Technologie IA exclusive • Accès limité',
    eu: 'AI teknologia esklusiboa • Sarbide mugatua',
    en: 'Exclusive AI Technology • Limited Access',
    es: 'Tecnología IA exclusiva • Acceso limitado',
    it: 'Tecnologia IA esclusiva • Accesso limitato',
    ja: '独占AI技術 • 限定アクセス',
    de: 'Exklusive KI-Technologie • Begrenzter Zugang',
    ru: 'Эксклюзивная ИИ-технология • Ограниченный доступ',
    ka: 'ექსკლუზიური AI ტექნოლოგია • შეზღუდული წვდომა'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await axios.post(`${API}/demo`, formData); setSuccess(true); } 
    catch (err) { console.error(err); } 
    finally { setLoading(false); }
  };

  return (
    <div data-testid="home-page" className="bg-[#0a0c10]">
      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <HeroBackground />
        <div className="container-asp relative z-20 py-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 backdrop-blur-sm px-6 py-2 rounded-full mb-8"
            >
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <span className="text-primary text-sm font-medium tracking-wider">{t('home.badge', language)}</span>
            </motion.div>
            
            {/* Tagline Hugues #1 */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-2xl md:text-3xl font-light text-secondary italic mb-6"
            >
              "{t('home.tagline1', language)}"
            </motion.p>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {t('home.title1', language)}<br />{t('home.title2', language)}<br /><span className="text-gradient">{t('home.title3', language)}</span>
            </h1>
            <p className="text-xl text-white/60 mb-10 max-w-2xl mx-auto leading-relaxed">
              {t('home.subtitle', language)}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link to="/contact"><Button className="btn-secondary text-base px-10 py-4 group" data-testid="hero-cta-btn">{t('home.cta', language)}<ArrowUpRight className="ml-2 w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></Button></Link>
              <Link to="/solution"><Button className="btn-outline text-base px-10 py-4" data-testid="hero-discover-btn">{t('home.discover', language)}</Button></Link>
            </div>
            
            {/* Logo + Baseline Marketing - sous le CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16 pt-12 mt-8 border-t border-white/10"
            >
              <div className="w-full lg:w-1/2 flex justify-center lg:justify-end">
                {/* Placeholder logo - à remplacer par le vrai logo PNG transparent */}
                <div className="h-48 md:h-64 lg:h-80 w-48 md:w-64 lg:w-80 rounded-2xl border-2 border-dashed border-white/20 flex items-center justify-center bg-white/5">
                  <span className="text-6xl md:text-7xl lg:text-8xl font-black text-white/40 tracking-tighter">ASP</span>
                </div>
              </div>
              <div className="w-full lg:w-1/2 text-center lg:text-left">
                <p className="text-white text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                  {marketingBaseline[language] || marketingBaseline['fr']}
                </p>
                <p className="text-primary text-lg md:text-xl font-semibold mt-3">
                  {marketingSubline[language] || marketingSubline['fr']}
                </p>
                {/* Marketing taglines */}
                <div className="mt-6 space-y-2">
                  <p className="text-secondary text-lg font-medium">{t('home.tagline4', language)}</p>
                  <p className="text-white/60 text-base italic">{t('home.tagline3', language)}</p>
                  <p className="text-white/50 text-sm">{t('home.tagline2', language)}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding bg-background" data-testid="stats-section">
        <div className="container-asp">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-secondary text-sm font-medium tracking-wider uppercase">{t('home.statsSubtitle', language)}</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mt-4 mb-6">{t('home.statsTitle', language)}</h2>
            <p className="text-white/50 max-w-2xl mx-auto text-lg">{t('home.statsDesc', language)}</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard icon={Volume2} value={t('home.stat1Value', language)} label={t('home.stat1Label', language)} desc={t('home.stat1Desc', language)} index={0} />
            <StatCard icon={Users} value={t('home.stat2Value', language)} label={t('home.stat2Label', language)} desc={t('home.stat2Desc', language)} index={1} />
            <StatCard icon={Target} value={t('home.stat3Value', language)} label={t('home.stat3Label', language)} desc={t('home.stat3Desc', language)} index={2} />
            <StatCard icon={Clock} value={t('home.stat4Value', language)} label={t('home.stat4Label', language)} desc={t('home.stat4Desc', language)} index={3} />
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="section-padding bg-gradient-to-b from-background to-black/50" data-testid="challenges-section">
        <div className="container-asp">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-primary text-sm font-medium tracking-wider uppercase">{t('home.challengesSubtitle', language)}</span>
            <h2 className="text-4xl font-bold text-white mt-4">{t('home.challengesTitle', language)}</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            <ChallengeCard title={t('home.challenge1Title', language)} desc={t('home.challenge1Desc', language)} example={t('home.challenge1Example', language)} index={0} />
            <ChallengeCard title={t('home.challenge2Title', language)} desc={t('home.challenge2Desc', language)} example={t('home.challenge2Example', language)} index={1} />
            <ChallengeCard title={t('home.challenge3Title', language)} desc={t('home.challenge3Desc', language)} example={t('home.challenge3Example', language)} index={2} />
          </div>
        </div>
      </section>

      {/* Briques */}
      <section className="section-padding bg-black/50" data-testid="briques-section">
        <div className="container-asp">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-secondary text-sm font-medium tracking-wider uppercase">{t('home.briquesSubtitle', language)}</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mt-4 mb-6">{t('home.briquesTitle', language)}</h2>
            <p className="text-white/50 max-w-2xl mx-auto">{t('home.briquesDesc', language)}</p>
          </motion.div>
          <div className="grid lg:grid-cols-3 gap-8">
            <BriqueCard icon={Ear} title={t('home.brique1Title', language)} subtitle={t('home.brique1Subtitle', language)} desc={t('home.brique1Desc', language)} features={[t('home.brique1F1', language), t('home.brique1F2', language), t('home.brique1F3', language)]} color="primary" index={0} />
            <BriqueCard icon={Brain} title={t('home.brique2Title', language)} subtitle={t('home.brique2Subtitle', language)} desc={t('home.brique2Desc', language)} features={[t('home.brique2F1', language), t('home.brique2F2', language), t('home.brique2F3', language)]} color="secondary" index={1} />
            <BriqueCard icon={Zap} title={t('home.brique3Title', language)} subtitle={t('home.brique3Subtitle', language)} desc={t('home.brique3Desc', language)} features={[t('home.brique3F1', language), t('home.brique3F2', language), t('home.brique3F3', language)]} color="accent" index={2} />
          </div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mt-12">
            <Link to="/solution"><Button className="btn-outline text-base px-8 py-3 group">{t('home.seeDetail', language)}<ArrowUpRight className="ml-2 w-4 h-4" /></Button></Link>
          </motion.div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-padding bg-background" data-testid="usecases-section">
        <div className="container-asp">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-primary text-sm font-medium tracking-wider uppercase">{t('home.usecasesSubtitle', language)}</span>
            <h2 className="text-4xl font-bold text-white mt-4">{t('home.usecasesTitle', language)}</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8">
            <UseCaseCard title={t('home.usecase1Title', language)} problem={t('home.usecase1Problem', language)} solution={t('home.usecase1Solution', language)} result={t('home.usecase1Result', language)} index={0} lang={language} />
            <UseCaseCard title={t('home.usecase2Title', language)} problem={t('home.usecase2Problem', language)} solution={t('home.usecase2Solution', language)} result={t('home.usecase2Result', language)} index={1} lang={language} />
            <UseCaseCard title={t('home.usecase3Title', language)} problem={t('home.usecase3Problem', language)} solution={t('home.usecase3Solution', language)} result={t('home.usecase3Result', language)} index={2} lang={language} />
            <UseCaseCard title={t('home.usecase4Title', language)} problem={t('home.usecase4Problem', language)} solution={t('home.usecase4Solution', language)} result={t('home.usecase4Result', language)} index={3} lang={language} />
          </div>
        </div>
      </section>

      {/* CTA Form */}
      <section className="section-padding bg-gradient-to-b from-background to-black/50 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 hero-gradient" />
        <div className="container-asp relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">{t('home.ctaTitle1', language)}<br /><span className="text-gradient">{t('home.ctaTitle2', language)}</span></h2>
              <p className="text-white/60 text-lg mb-8 leading-relaxed">{t('home.ctaDesc', language)}</p>
              <ul className="space-y-3 text-white/50">
                <li className="flex items-center gap-3"><Check className="w-5 h-5 text-primary" />{t('home.ctaF1', language)}</li>
                <li className="flex items-center gap-3"><Check className="w-5 h-5 text-primary" />{t('home.ctaF2', language)}</li>
                <li className="flex items-center gap-3"><Check className="w-5 h-5 text-primary" />{t('home.ctaF3', language)}</li>
                <li className="flex items-center gap-3"><Check className="w-5 h-5 text-primary" />{t('home.ctaF4', language)}</li>
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Card className="card-glass p-8">
                {success ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-primary" /></div>
                    <h3 className="text-2xl font-semibold text-white mb-2">{t('home.formSuccess', language)}</h3>
                    <p className="text-white/50">{t('home.formSuccessDesc', language)}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><label className="text-sm text-white/50 mb-2 block">{t('home.formClub', language)}</label><Input required value={formData.club_name} onChange={(e) => setFormData({ ...formData, club_name: e.target.value })} placeholder={t('home.formClubPlaceholder', language)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="demo-club-input" /></div>
                      <div><label className="text-sm text-white/50 mb-2 block">{t('home.formName', language)}</label><Input required value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} placeholder={t('home.formNamePlaceholder', language)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="demo-name-input" /></div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><label className="text-sm text-white/50 mb-2 block">{t('home.formEmail', language)}</label><Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={t('home.formEmailPlaceholder', language)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="demo-email-input" /></div>
                      <div><label className="text-sm text-white/50 mb-2 block">{t('home.formPhone', language)}</label><Input type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder={t('home.formPhonePlaceholder', language)} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="demo-phone-input" /></div>
                    </div>
                    <div><label className="text-sm text-white/50 mb-2 block">{t('home.formLevel', language)}</label><Select value={formData.level} onValueChange={(v) => setFormData({ ...formData, level: v })}><SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="demo-level-select"><SelectValue placeholder={t('home.formLevelPlaceholder', language)} /></SelectTrigger><SelectContent><SelectItem value="amateur">{t('home.formLevelAmateur', language)}</SelectItem><SelectItem value="semi_pro">{t('home.formLevelSemiPro', language)}</SelectItem><SelectItem value="pro">{t('home.formLevelPro', language)}</SelectItem></SelectContent></Select></div>
                    <Button type="submit" className="w-full btn-secondary py-4" disabled={loading || !formData.level} data-testid="demo-submit-btn">{loading ? t('common.sending', language) : t('home.formSubmit', language)}</Button>
                    <p className="text-white/30 text-xs text-center">{t('home.formNote', language)}</p>
                  </form>
                )}
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-16 bg-background border-y border-white/5">
        <div className="container-asp">
          {/* Tagline Hugues #3 */}
          <motion.p 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            viewport={{ once: true }}
            className="text-center text-2xl md:text-3xl font-light text-white/70 italic mb-12"
          >
            "{t('home.tagline3', language)}"
          </motion.p>
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
            <p className="text-white/30 text-sm uppercase tracking-wider">{t('home.trustTitle', language)}</p>
            <div className="flex items-center gap-8">
              {/* Placeholder logos - à remplacer par les vrais logos PNG transparents */}
              <div className="h-16 w-32 rounded-lg border border-dashed border-white/20 flex items-center justify-center bg-white/5">
                <span className="text-sm font-bold text-white/30">CLUB 1</span>
              </div>
              <div className="h-16 w-32 rounded-lg border border-dashed border-white/20 flex items-center justify-center bg-white/5">
                <span className="text-sm font-bold text-white/30">CLUB 2</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
